import { getWishlistIds } from "./api-service";
import { vibrate } from "./util-service";

const WISHLIST_KEY = "wishlist";
const WISHLIST_SYNCED_KEY = "wishlistSynced";

const normalizeWishlistIds = (raw) => {
  if (!Array.isArray(raw)) return [];
  return [
    ...new Set(
      raw
        .map((item) => (typeof item === "string" ? item : item?.id))
        .filter(Boolean),
    ),
  ];
};

export const getWishlist = () => {
  try {
    const wishlist = localStorage.getItem(WISHLIST_KEY);
    const ids = normalizeWishlistIds(wishlist ? JSON.parse(wishlist) : []);
    // Migrate legacy full-product entries to id-only storage
    if (wishlist && JSON.stringify(ids) !== wishlist) {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(ids));
    }
    return ids;
  } catch (error) {
    console.error("Error reading wishlist:", error);
    return [];
  }
};

export const setWishlist = (wishlist) => {
  localStorage.setItem(
    WISHLIST_KEY,
    JSON.stringify(normalizeWishlistIds(wishlist)),
  );
};

export const isInWishlist = (productId) => {
  if (!productId) return false;
  return getWishlist().includes(productId);
};

export const addToWishlist = (productId) => {
  const id = typeof productId === "string" ? productId : productId?.id;
  if (!id) return getWishlist();

  const wishlist = getWishlist();
  if (wishlist.includes(id)) {
    return wishlist;
  }
  vibrate(30);
  const next = [...wishlist, id];
  setWishlist(next);
  return next;
};

export const removeFromWishlist = (productId) => {
  const next = getWishlist().filter((id) => id !== productId);
  setWishlist(next);
  vibrate(30);
  return next;
};

export const toggleWishlistProduct = (productId) => {
  const id = typeof productId === "string" ? productId : productId?.id;
  if (!id) return getWishlist();

  if (isInWishlist(id)) {
    return removeFromWishlist(id);
  }

  return addToWishlist(id);
};

export const clearWishlist = () => {
  localStorage.removeItem(WISHLIST_KEY);
};

export const markWishlistSynced = () => {
  sessionStorage.setItem(WISHLIST_SYNCED_KEY, "1");
};

export const clearWishlistSynced = () => {
  sessionStorage.removeItem(WISHLIST_SYNCED_KEY);
};

export const isWishlistSynced = () =>
  sessionStorage.getItem(WISHLIST_SYNCED_KEY) === "1";

/**
 * Fetch all wishlist product ids from API and replace localStorage.
 * @returns {Promise<string[]>}
 */
export const syncWishlistIdsFromApi = async () => {
  try {
    const response = await getWishlistIds();
    if (response?.status !== 200) {
      return getWishlist();
    }

    const ids = normalizeWishlistIds(response?.data?.productIds);
    setWishlist(ids);
    markWishlistSynced();
    return ids;
  } catch (error) {
    console.error("Error syncing wishlist ids:", error);
    return getWishlist();
  }
};

/**
 * Sync once per browser session (avoids refetch on every MainLayout remount).
 * @returns {Promise<string[]>}
 */
export const syncWishlistIdsOncePerSession = async () => {
  if (isWishlistSynced()) {
    return getWishlist();
  }
  return syncWishlistIdsFromApi();
};
